/**
 * Product catalog line items for a bidding option (mirrors quote line items).
 */
import React from "react";
import {
  Box,
  TextField,
  IconButton,
  Button,
  Typography,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import PriceCardLineItemEditor from "@/features/priceCard/components/PriceCardLineItemEditor.jsx";
import SupplierSelect from "../../../../shared/components/SupplierSelect.jsx";

export const defaultOptionLineItem = () => ({
  type: "text",
  description: "",
  quantity: 1,
  productId: null,
  product: null,
  isCustom: true,
  supplier: "bubakii",
  unitPrice: "",
  total: 0,
});

const recalcTotal = (item) => {
  const quantity = Number(item.quantity) || 0;
  const unitPrice =
    item.unitPrice === "" || item.unitPrice === null
      ? 0
      : Number(item.unitPrice) || 0;
  return Math.round(quantity * unitPrice * 100) / 100;
};

const OptionLineItemsEditor = ({ lineItems, onChange, disabled = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const items =
    lineItems?.length > 0 ? lineItems : [defaultOptionLineItem()];

  const updateItem = (index, patch) => {
    const next = items.map((item, i) => {
      if (i !== index) return item;
      const updated = { ...item, ...patch };
      updated.total = recalcTotal(updated);
      return updated;
    });
    onChange(next);
  };

  const addRow = () => {
    onChange([...items, defaultOptionLineItem()]);
  };

  const removeRow = (index) => {
    if (items.length <= 1) {
      onChange([defaultOptionLineItem()]);
      return;
    }
    onChange(items.filter((_, i) => i !== index));
  };

  return (
    <Stack spacing={1.5}>
      <Typography variant="caption" color="text.secondary">
        Add products (qty + supplier)
      </Typography>
      {items.map((item, index) => (
        <Box
          key={index}
          sx={{
            p: { xs: 0.75, sm: 1 },
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography variant="caption" fontWeight="bold">
              Product {index + 1}
            </Typography>
            <IconButton
              size="small"
              color="error"
              onClick={() => removeRow(index)}
              disabled={disabled}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Box>
          <Stack spacing={1}>
            <PriceCardLineItemEditor
              lineItem={item}
              onLineItemChange={(nextItem) => updateItem(index, nextItem)}
              label="Tariff, product or one-off item"
              disabled={disabled}
            />
            <TextField
              size="small"
              fullWidth
              label="Description"
              value={item.description}
              onChange={(e) => updateItem(index, { description: e.target.value })}
              disabled={disabled}
            />
            {isMobile ? (
              <Stack spacing={1}>
                <Stack direction="row" spacing={1}>
                  <TextField
                    size="small"
                    label="Qty"
                    type="number"
                    value={item.quantity ?? 1}
                    onChange={(e) =>
                      updateItem(index, { quantity: Number(e.target.value) || 1 })
                    }
                    inputProps={{ min: 1, step: 1 }}
                    sx={{ flex: 1 }}
                    disabled={disabled}
                  />
                  <TextField
                    size="small"
                    label="Unit (R)"
                    type="number"
                    value={item.unitPrice}
                    onChange={(e) =>
                      updateItem(index, { unitPrice: e.target.value })
                    }
                    inputProps={{ min: 0, step: 0.01 }}
                    sx={{ flex: 1 }}
                    disabled={disabled}
                  />
                </Stack>
                <SupplierSelect
                  value={item.supplier || "bubakii"}
                  onChange={(supplier) => updateItem(index, { supplier })}
                  defaultSupplier={item.product?.defaultSupplier}
                  label="Supplier"
                  disabled={disabled}
                  fullWidth
                />
              </Stack>
            ) : (
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "flex-start" }}>
                <TextField
                  size="small"
                  label="Qty"
                  type="number"
                  value={item.quantity ?? 1}
                  onChange={(e) =>
                    updateItem(index, { quantity: Number(e.target.value) || 1 })
                  }
                  inputProps={{ min: 1, step: 1 }}
                  sx={{ width: 72 }}
                  disabled={disabled}
                />
                <TextField
                  size="small"
                  label="Unit (R)"
                  type="number"
                  value={item.unitPrice}
                  onChange={(e) =>
                    updateItem(index, { unitPrice: e.target.value })
                  }
                  inputProps={{ min: 0, step: 0.01 }}
                  sx={{ width: 100 }}
                  disabled={disabled}
                />
                <Box sx={{ flex: 1, minWidth: 160 }}>
                  <SupplierSelect
                    value={item.supplier || "bubakii"}
                    onChange={(supplier) => updateItem(index, { supplier })}
                    defaultSupplier={item.product?.defaultSupplier}
                    label="Supplier"
                    disabled={disabled}
                  />
                </Box>
              </Box>
            )}
            {item.total > 0 && (
              <Typography variant="caption" color="text.secondary">
                Line total: R {Number(item.total).toLocaleString()}
              </Typography>
            )}
          </Stack>
        </Box>
      ))}
      <Button
        size="small"
        startIcon={<Add />}
        onClick={addRow}
        disabled={disabled}
      >
        Add product
      </Button>
    </Stack>
  );
};

/** Strip UI-only fields before API payload */
export function serializeOptionLineItems(lineItems) {
  return (lineItems || [])
    .filter((item) => item.description?.trim())
    .map((item) => ({
      type: "text",
      description: item.description.trim(),
      quantity: Number(item.quantity) || 1,
      productId: item.productId || null,
      priceCardElementId: item.priceCardElementId || null,
      priceCardId: item.priceCardId || null,
      priceCardVersion: item.priceCardVersion || null,
      unit: item.unit || null,
      pricingOverride: item.pricingOverride || undefined,
      isCustom: item.isCustom !== false,
      supplier: item.supplier || "bubakii",
      unitPrice:
        item.unitPrice === "" || item.unitPrice === null
          ? 0
          : Number(item.unitPrice),
    }));
}

/** Hydrate saved line items for the editor */
export function hydrateOptionLineItems(lineItems) {
  if (!lineItems?.length) {
    return [defaultOptionLineItem()];
  }
  return lineItems.map((item) => ({
    ...defaultOptionLineItem(),
    ...item,
    product: null,
    unitPrice: item.unitPrice ?? "",
    total: item.total ?? recalcTotal(item),
  }));
}

export default OptionLineItemsEditor;
